import {
  isHmBrand,
  getTouchpoint,
  getCookie,
  parseLocale,
  getLocale,
  currentBrand,
  trackEvent,
} from '@pb/utils'

import appSelectors from 'App/duck/selectors'
import clubCardSelectors from 'features/account/club-card/duck/selectors'
import myProfileSelectors from 'features/myProfile/duck/selectors'
import toUppercasedBooleanString from 'helpers/toUppercasedBooleanString'
import { RootState } from 'redux/types'

import { getFavouritesItems } from './duck/selectors'
import { NormalizedFavourites } from './duck/types'

declare global {
  interface Window {
    getRegionMarket: Function
  }
}

/** *********************
 * CONSTANTS
 ********************** */

export const EVENT_TYPES = {
  favourites_view: 'VIEW_FAVOURITES',
  add_to_bag: 'ADD_TO_BAG',
  remove_from_favourites: 'REMOVE_FROM_FAVOURITES',
  choose_size: 'FAVOURITE_CHOOSE_SIZE',
  following_styleboards_view: 'VIEW_FOLLOWING_STYLEBOARDS',
  my_styleboards_view: 'VIEW_MY_STYLEBOARDS',
  stylestories_view: 'VIEW_STYLESTORIES',
  my_closet_view: 'VIEW_MY_CLOSET',
  favourites_view_empty: 'VIEW_FAVOURITES_EMPTY',
}

const PROMO_ACTIONS = {
  PROMO_VIEW: 'promoview',
  PROMO_CLICK: 'promo_click',
}

export const PAGE_ID = {
  favourites: 'MY FAVOURITES',
  following_styleboards: 'FOLLOWING STYLEBOARDS',
  my_styleboards: 'MY STYLEBOARDS',
  stylestories: 'STYLESTORIES',
  my_closet: 'MY CLOSET',
}

export const CATEGORY_PATH = {
  favourites: 'FAVOURITES',
  following_styleboards: 'FOLLOWING_STYLEBOARDS',
  my_styleboards: 'MY_STYLEBOARDS',
  stylestories: 'STYLESTORIES',
  my_closet: 'MY_CLOSET',
}

/** *********************
 * FAVOURITES TRACKING
 ********************** */

// Cross brand reusable tracking
const getGeneralTracking = (state, eventType: string): object => ({
  event_type: eventType,
  category_id: 'FAVOURITES',
  category_path: CATEGORY_PATH.favourites,
  customer_bpid: appSelectors.getUserBpid(state) || '',
  customer_id: appSelectors.getUserId(state) || '',
  customer_city: appSelectors.getUserCity(state)?.toUpperCase() || '',
  customer_country: appSelectors.getUserCountryCode(state) || '',
  customer_email: appSelectors.getUserEmail(state) || '',
  customer_state: appSelectors.getUserState(state)?.toUpperCase() || '',
  customer_zip: appSelectors.getUserPostalCode(state),
})

/** *********************
 * FAVOURITES TRACKING
 ********************** */

// H&M reusable tracking
const getHmSpecificTracking = (state): object => ({
  page_id: PAGE_ID.favourites,
  selected_market: parseLocale(getLocale()).country?.toUpperCase() || '',
  touchpoint: getTouchpoint(),
  customer_fashion_news:
    appSelectors.getIsFashionNewsEnabled(state)?.toString()?.toUpperCase() || '',
  customer_loyalty_level: appSelectors.getCustomerLoyaltyLevel(state) || '',
  customer_has_children: appSelectors.getUserHasChildren(state) || '',
  customer_status: appSelectors.getUserStatus(state) || '',
  customer_club_member: appSelectors.getUserIsFullMember(state)?.toString()?.toUpperCase() || '',
  customer_club_level: `tier ${clubCardSelectors.getCurrentTier(state)}`,
  validated_user: appSelectors.getValidatedUser(state) || false,
  profile_id: myProfileSelectors.getProfileId(state) || '',
  dep_variable_name: ['validated_user', 'profile_id'],
  dep_variable_format: ['boolean', 'string'],
})

// Portfolio brands reusable tracking
const getBrandSpecificTracking = (): object => ({
  page_id: 'My Favourites',
  page_type: 'favourites',
  region_locale: getCookie('HMCORP_locale')?.toLowerCase() || '',
  region_market: window && window.getRegionMarket ? window.getRegionMarket().toLowerCase() : '',
  session_login_status:
    document.getElementsByClassName('is-logged-in')?.length > 0 ? 'TRUE' : 'FALSE',
  session_touchpoint: getTouchpoint(),
  region_currency: getCookie('HMCORP_currency')?.toLowerCase() || '',
})

// H&M multiple products tracking
const getFavouritesHmSpecificProductsTrackingInfo = (state: RootState): object => {
  const items = getFavouritesItems(state) as NormalizedFavourites[]
  return {
    ...(items && {
      product_article_id: items.map(({ articleCode }) => articleCode || '') || [''],
      product_category: items.map(({ category }) => category?.toUpperCase() || '') || [''],
      product_color_code: items.map(
        ({ sizePickerData }) => sizePickerData?.item?.color?.code || '',
      ) || [''],
      product_currency: items[0]?.currency || appSelectors.getCurrentCurrency(state)?.isocode || '',
      product_few_pieces: items.map(
        ({ sizePickerData }) => sizePickerData?.item?.fewPieceLeft?.toString()?.toUpperCase() || '',
      ) || [''],
      product_id: items.map(
        ({ sizePickerData }) => sizePickerData?.item?.baseProductCode || '',
      ) || [''],
      product_name: items.map(({ title }) => title?.toUpperCase() || '') || [''],
      product_brand: items.map(({ brandName }) => brandName?.toUpperCase() || 'H&M'),
      product_video: items.map((item) => toUppercasedBooleanString(item.productVideo) || ''),
      product_marker_marketing: items.map(
        ({ marketingMarkerText }) => marketingMarkerText || 'FALSE',
      ),
      product_marker_selection: items.map(({ newProductLabel }) => newProductLabel || 'FALSE'),
    }),
  }
}

// Portfolio brands multiple products tracking
const getFavouritesBrandSpecificProductsTrackingInfo = (state: RootState): object => {
  const items = getFavouritesItems(state) as NormalizedFavourites[]
  return {
    ...(items && items.length !== 0 && {
      product_id: items.map(({ articleCode }) => articleCode || '') || [''],
      product_name: items.map(
        ({ title, articleCode }) => (title && `${title} | ${articleCode}`) || '',
      ) || [''],
      product_brand: [
        currentBrand?.toUpperCase() === 'OTHERSTORIES'
          ? 'STORIES'
          : currentBrand?.toUpperCase() || '',
      ],
      product_category: items.map(({ category }) => category || '') || [''],
      product_price: items.map(
        ({ redPriceValue, yellowPriceValue, regularPriceValue }) =>
          redPriceValue || yellowPriceValue || regularPriceValue || '',
      ) || [''],
      product_original_price: items.map(({ regularPriceValue }) => regularPriceValue || '') || [''],
      product_color: items.map(({ sizePickerData }) => sizePickerData?.item?.color?.text || '') || [
        '',
      ],
      product_color_code: items.map(
        ({ sizePickerData }) => sizePickerData?.item?.color?.code || '',
      ) || [''],
      product_size: items.map(({ sizePickerData }) => {
        const selectedVariant = sizePickerData?.item?.variants?.find(
          (variant) => variant.selected === true,
        )
        return selectedVariant?.size?.name || ''
      }) || [''],
      product_size_code: items.map(({ sizePickerData }) => {
        const selectedVariant = sizePickerData?.item?.variants?.find(
          (variant) => variant.selected === true,
        )
        return selectedVariant?.size?.sizeCode || ''
      }) || [''],
      product_currency: items[0]?.currency || getCookie('HMCORP_currency')?.toLowerCase() || '',
    }),
  }
}

// H&M one product tracking
const getFavouritesHmSpecificProductTrackingInfo = (
  variantCode: string | number,
  state: RootState,
): object => {
  const items = getFavouritesItems(state) as NormalizedFavourites[]
  const favouritesItem = (items || []).find((item) => item.code === variantCode)

  if (favouritesItem) {
    const favouritesVariantData = favouritesItem.sizePickerData?.item?.variants?.find(
      (variant) => variant.code === variantCode,
    )

    return {
      product_article_id: [favouritesItem.articleCode || ''],
      product_category: [favouritesItem.category?.toUpperCase() || ''],
      product_list_price: [
        favouritesItem.redPriceValue?.toString() ||
          favouritesItem.yellowPriceValue?.toString() ||
          favouritesItem.regularPriceValue?.toString() ||
          '',
      ],
      product_name: [favouritesItem.title?.toUpperCase() || ''],
      product_original_price: [
        favouritesItem?.sizePickerData?.item?.ordinaryPrice?.value?.toString()?.toUpperCase() || '',
      ],
      product_color_code: [favouritesItem?.sizePickerData?.item?.color?.code || ''],
      product_currency: favouritesItem?.currency,
      product_few_pieces: [
        favouritesItem?.sizePickerData?.item?.fewPieceLeft?.toString()?.toUpperCase() || '',
      ],
      product_id: [favouritesItem?.sizePickerData?.item?.baseProductCode || ''],
      ...(favouritesVariantData && {
        product_size: [favouritesVariantData?.size?.name || ''],
        product_size_code: [favouritesVariantData?.size?.code || ''],
      }),
      product_brand: [favouritesItem.brandName?.toUpperCase() || 'H&M'],
      product_video: [toUppercasedBooleanString(favouritesItem.productVideo) || ''],
      product_marker_marketing: [favouritesItem.marketingMarkerText || 'FALSE'],
      product_marker_selection: [favouritesItem.newProductLabel || 'FALSE'],
    }
  }
  return {}
}

// Portfolio brands one product tracking
const getFavouritesBrandSpecificProductTrackingInfo = (
  variantCode: string | number,
  state: RootState,
  productAction?: string,
): object => {
  const items = getFavouritesItems(state) as NormalizedFavourites[]
  const favouritesItem = (items || []).find((item) => item.code === variantCode)

  if (favouritesItem) {
    const favouritesVariantData = favouritesItem.sizePickerData?.item?.variants?.find(
      (variant) => variant.code === variantCode,
    )

    return {
      ...(productAction && { product_action: productAction }),
      product_id: [favouritesItem?.articleCode || ''],
      product_name: [
        favouritesItem?.title ? `${favouritesItem.title} | ${favouritesItem?.articleCode}` : '',
      ],
      product_brand: [
        currentBrand?.toUpperCase() === 'OTHERSTORIES'
          ? 'STORIES'
          : currentBrand?.toUpperCase() || '',
      ],
      product_category: [favouritesItem?.category || ''],
      product_price: [
        favouritesItem?.redPriceValue ||
          favouritesItem?.yellowPriceValue ||
          favouritesItem?.regularPriceValue ||
          '',
      ],
      product_original_price: [favouritesItem?.regularPriceValue || ''],
      product_color: [favouritesItem?.sizePickerData?.item?.color?.text || ''],
      product_color_code: [favouritesItem?.sizePickerData?.item?.color?.code || ''],
      product_size: [favouritesVariantData?.size?.name || ''],
      product_size_code: [favouritesVariantData?.size?.sizeCode || ''],
      product_currency:
        favouritesItem?.currency || getCookie('HMCORP_currency')?.toLowerCase() || '',
    }
  }
  return {}
}

export const getFavouritesTracking = (state, eventType: string): object => {
  const generalTracking = getGeneralTracking(state, eventType)

  if (!isHmBrand) {
    const brandSpecificTracking = getBrandSpecificTracking()
    const brandSpecificTrackingMultipleItems = getFavouritesBrandSpecificProductsTrackingInfo(state)

    return {
      ...generalTracking,
      ...brandSpecificTracking,
      ...brandSpecificTrackingMultipleItems,
    }
  }

  const hmSpecificTracking = getHmSpecificTracking(state)
  const hmSpecificTrackingMultipleItems = getFavouritesHmSpecificProductsTrackingInfo(state)

  return {
    ...generalTracking,
    ...hmSpecificTracking,
    ...hmSpecificTrackingMultipleItems,
    customer_username: appSelectors.getUserEmail(state) || '',
  }
}

export const getFavouritesEventTracking = (
  variantCode,
  state,
  eventType: string,
  productAction?: string,
): object => {
  const generalTracking = getGeneralTracking(state, eventType)

  if (!isHmBrand) {
    const brandSpecificTracking = getBrandSpecificTracking()
    const brandSpecificTrackingSingleItem = getFavouritesBrandSpecificProductTrackingInfo(
      variantCode,
      state,
      productAction,
    )

    return {
      ...generalTracking,
      ...brandSpecificTracking,
      ...brandSpecificTrackingSingleItem,
    }
  }

  const hmSpecificTracking = getHmSpecificTracking(state)
  const hmSpecificTrackingSingleItem = getFavouritesHmSpecificProductTrackingInfo(
    variantCode,
    state,
  )

  return {
    ...generalTracking,
    ...hmSpecificTracking,
    ...hmSpecificTrackingSingleItem,
  }
}

export const trackViewFavouritesEmptyButton = (): void => {
  trackEvent({
    ...getBrandSpecificTracking,
    promo_action: [PROMO_ACTIONS.PROMO_VIEW] || [],
    promo_id: ['favourites page'] || [],
    promo_name: ['button'] || [],
    promo_creative: ['button.favourites.empty'] || [],
  })
}

export const trackClickFavouritesEmpty = (): void => {
  trackEvent({
    ...getBrandSpecificTracking,
    promo_action:[ PROMO_ACTIONS.PROMO_CLICK] || [],
    promo_id: ['favourites page'] || [],
    promo_name: ['button'] || [],
    promo_creative: ['button.favourites.empty'] || [],
  })
}


/** *********************
 * TAB TRACKING
 ********************** */

export const getTabTracking = (
  eventType: string,
  pageId: string,
  categoryPath: string,
): object => ({
  event_type: eventType,
  category_id: CATEGORY_PATH.favourites,
  categoryPath: `${CATEGORY_PATH.favourites}/${categoryPath}`,
  category_path: `${CATEGORY_PATH.favourites}/${categoryPath}`,
  page_id: pageId,
  selected_market: parseLocale(getLocale()).country?.toUpperCase() || '',
  display_language: parseLocale(getLocale()).locale?.toUpperCase() || '',
  touchpoint: getTouchpoint(),
})

export const trackProductStoreLocatorClick = (variantCode?: string | number): void => {
  trackEvent({
    event_label: variantCode,
    event_category: 'Store',
    event_id: 'Find favorite in store',
    event_type: 'CLICK_FIND_FAVORITE_IN_STORE',
  })
}

export const trackProductStoreLocatorWithSizeClick = (variantCode?: string | number): void => {
  trackEvent({
    event_label: variantCode,
    event_category: 'Store',
    event_id: 'Find size in store',
    event_type: 'CLICK_FIND_FAVORITE_IN_STORE',
  })
}

export const trackSearchLocationFindInStore = (searchedLocation?: string): void => {
  trackEvent({
    event_label: searchedLocation,
    event_category: 'Store',
    event_id: 'Search find in store',
    event_type: 'SEARCH_FIND_IN_STORE',
  })
}

export const trackCloseFindInStore = (eventId: string): void => {
  trackEvent({
    event_category: 'Store',
    event_id: eventId,
    event_type: 'CLOSE_FIND_IN_STORE',
  })
}

export const trackSearchResultFindInStore = (
  variantCode?: string | number,
  eventId?: string,
): void => {
  trackEvent({
    event_label: variantCode,
    event_category: 'Store',
    event_id: eventId,
    event_type: 'SEARCHRESULT_FIND_IN_STORE',
  })
}

export const trackClickOnStoreFindInStore = (storeId?: string | number): void => {
  trackEvent({
    event_label: storeId,
    event_category: 'Store',
    event_id: 'Get store',
    event_type: 'CHOOSE_STORE',
  })
}

export const trackPhonenumberFindInStore = (): void => {
  trackEvent({
    event_category: 'Store',
    event_id: 'Get store',
    event_type: 'CLICK_ON_PHONE_NR',
  })
}

export const trackStoreInformationFindInStore = (eventId?: string, eventType?: string): void => {
  trackEvent({
    event_category: 'Store',
    event_id: eventId,
    event_type: eventType,
  })
}

export const handleTrackShowNibModal = (variantCode: string | number): void => {
  trackEvent({
    event_category: 'notify me',
    event_id: 'initiate - favorites',
    event_type: 'NIB_INITIATE',
    event_label: variantCode,
  })
}

export const findInStoreToggleSwitch = (eventId: string): void => {
  trackEvent({
    event_category: 'Store',
    event_id: eventId,
    event_type: 'FIND_IN_STORE_TOGGLE_SWITCH',
  })
}

export const trackUserAllowLocationFindInStore = (): void => {
  trackEvent({
    event_category: 'Store',
    event_id: 'Click allow geo location',
    event_type: 'CLICK_ALLOW_GEO_LOCATION',
  })
}

export const trackUserBlockLocationFindInStore = (): void => {
  trackEvent({
    event_category: 'Store',
    event_id: 'Click block geo location',
    event_type: 'CLICK_BLOCK_GEO_LOCATION',
  })
}

export const trackClickOnViewAllPublicStyleboards = (): void => {
  trackEvent({
    event_category: 'styleboard',
    event_id: 'click styleboards view all component',
    event_label: 'view all styleboards - following pill',
  })
}
